import React, { useEffect, useState, useMemo } from 'react';
import { getAllDriverLaps, getDriverLapData, getAllTracks, getAllDrivers, getLatestSession } from '../services/api';
import { Bar, Line, Radar } from 'react-chartjs-2';
import 'chart.js/auto';
import './DriverLapAnalysis.css';
import Loader from './Loader';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ModernSelect from './ModernSelect';
import StatisticsPanel from './StatisticsPanel';
import PaceAnalysis from './PaceAnalysis';
import { FaFlagCheckered, FaChartLine } from 'react-icons/fa';

// Team color mapping
const teamColors = {
    'Red Bull Racing': '#1e41ff',
    'Red Bull': '#1e41ff',
    'McLaren': '#ff8700',
    'Mercedes': '#00d2be',
    'Ferrari': '#dc143c',
    'Aston Martin': '#00665e',
    'Williams': '#005aff',
    'RB': '#1e41ff',
    'Visa Cash App RB': '#1e41ff',
    'AlphaTauri': '#1e41ff',
    'Kick Sauber': '#900000',
    'Alfa Romeo': '#900000',
    'Haas F1 Team': '#ffffff',
    'Haas': '#ffffff',
    'Alpine': '#2b4562',
    'Force India': '#ff69b4',
    'Racing Point': '#f596c8',
};

// Default colors for drivers without team info
const defaultColors = ['#36a2eb', '#ff6384', '#ffce56', '#4bc0c0', '#9966ff', '#ff9f40'];

const analysisModes = [
    { value: 'single', label: 'Single Lap' },
    { value: 'multi', label: 'Multi-Lap Analysis' },
    { value: 'trends', label: 'Lap Trends' },
];

// Helper function to add alpha to hex color
const addAlpha = (color, alpha) => {
    if (color && color.startsWith('#')) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color || 'rgba(54, 162, 235, 0.25)';
};

const DriverLapAnalysis = () => {
    // Dynamic data state
    const [tracks, setTracks] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loadingTracks, setLoadingTracks] = useState(true);
    const [loadingDrivers, setLoadingDrivers] = useState(false);
    const [currentSessionKey, setCurrentSessionKey] = useState(null);

    // Single lap state
    const [lapDataDriver1, setLapDataDriver1] = useState(null);
    const [lapDataDriver2, setLapDataDriver2] = useState(null);
    const [loadingDriver1, setLoadingDriver1] = useState(true);
    const [loadingDriver2, setLoadingDriver2] = useState(true);
    const [errorDriver1, setErrorDriver1] = useState(null);
    const [errorDriver2, setErrorDriver2] = useState(null);

    // Multi-lap state
    const [allLapsDriver1, setAllLapsDriver1] = useState([]);
    const [allLapsDriver2, setAllLapsDriver2] = useState([]);
    const [loadingAllLaps1, setLoadingAllLaps1] = useState(false);
    const [loadingAllLaps2, setLoadingAllLaps2] = useState(false);

    // UI state
    const [currentLap, setCurrentLap] = useState(2);
    const [selectedDriver1, setSelectedDriver1] = useState(null);
    const [selectedDriver2, setSelectedDriver2] = useState(null);
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [analysisMode, setAnalysisMode] = useState('single');
    const [lapRange, setLapRange] = useState({ start: 1, end: 20 });

    // Load tracks on mount
    useEffect(() => {
        const loadTracks = async () => {
            try {
                setLoadingTracks(true);
                const tracksData = await getAllTracks();
                setTracks(tracksData);
                if (tracksData.length > 0) {
                    setSelectedTrack(tracksData[0].country_name);
                }
            } catch (error) {
                console.error('Error loading tracks:', error);
            } finally {
                setLoadingTracks(false);
            }
        };
        loadTracks();
    }, []);

    // Load drivers when track is selected
    useEffect(() => {
        const loadDrivers = async () => {
            if (!selectedTrack) {
                setDrivers([]);
                setSelectedDriver1(null);
                setSelectedDriver2(null);
                return;
            }
            
            try {
                setLoadingDrivers(true);
                setDrivers([]);
                setSelectedDriver1(null);
                setSelectedDriver2(null);
                
                const sessionData = await getLatestSession(selectedTrack);
                if (sessionData && sessionData.session_key) {
                    setCurrentSessionKey(sessionData.session_key);
                    const driversData = await getAllDrivers(sessionData.session_key);
                    
                    if (!driversData || driversData.length === 0) {
                        console.warn('No drivers found for this session');
                        return;
                    }
                    
                    // Add colors and images to drivers
                    const driversWithColors = driversData.map((driver, index) => {
                        if (!driver || !driver.id) return null;
                        
                        const teamColor = driver.team ? teamColors[driver.team] : null;
                        const color = teamColor || defaultColors[index % defaultColors.length];
                        
                        // Try to load driver image, fallback to placeholder
                        let img = null;
                        // Dynamic require doesn't work well, so we'll use a placeholder or try static paths
                        // Images will be handled via a mapping or default placeholder
                        
                        return {
                            ...driver,
                            color,
                            img
                        };
                    }).filter(driver => driver !== null);
                    
                    setDrivers(driversWithColors);
                    if (driversWithColors.length > 0) {
                        setSelectedDriver1(driversWithColors[0].id);
                        if (driversWithColors.length > 1) {
                            setSelectedDriver2(driversWithColors[1].id);
                        } else {
                            setSelectedDriver2(driversWithColors[0].id);
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading drivers:', error);
                setDrivers([]);
            } finally {
                setLoadingDrivers(false);
            }
        };
        loadDrivers();
    }, [selectedTrack]);

    // Fetch single lap data
    const fetchLapData = async (driverId, setLapData, setLoading, setError, selectedTrack) => {
        try {
            setLoading(true);
            setError(null);
            const result = await getDriverLapData(driverId, currentLap, selectedTrack);
            if (result && result.length > 0) {
                const lap = result[0];
                const relevantLapData = {
                    lap_number: lap.lap_number || 0,
                    lap_duration: lap.lap_duration || 0,
                    duration_sector_1: lap.duration_sector_1 || 0,
                    duration_sector_2: lap.duration_sector_2 || 0,
                    duration_sector_3: lap.duration_sector_3 || 0,
                };
                setLapData(relevantLapData);
            } else {
                throw new Error('No Lap Data Found');
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch all laps for multi-lap analysis
    const fetchAllLaps = async (driverId, setLaps, setLoading, selectedTrack) => {
        try {
            setLoading(true);
            const result = await getAllDriverLaps(driverId, selectedTrack);
            if (result && result.length > 0) {
                const validLaps = result
                    .filter(lap => lap.lap_duration && lap.lap_duration > 0)
                    .sort((a, b) => a.lap_number - b.lap_number);
                setLaps(validLaps);
            } else {
                setLaps([]);
            }
        } catch (error) {
            console.error(`Error fetching all laps for driver ${driverId}:`, error);
            setLaps([]);
        } finally {
            setLoading(false);
        }
    };

    // Effect for single lap mode
    useEffect(() => {
        if (analysisMode === 'single' && selectedDriver1 && selectedDriver2 && selectedTrack) {
            fetchLapData(selectedDriver1, setLapDataDriver1, setLoadingDriver1, setErrorDriver1, selectedTrack);
            fetchLapData(selectedDriver2, setLapDataDriver2, setLoadingDriver2, setErrorDriver2, selectedTrack);
        }
    }, [currentLap, selectedDriver1, selectedDriver2, selectedTrack, analysisMode]);

    // Effect for multi-lap mode
    useEffect(() => {
        if ((analysisMode === 'multi' || analysisMode === 'trends') && selectedDriver1 && selectedDriver2 && selectedTrack) {
            fetchAllLaps(selectedDriver1, setAllLapsDriver1, setLoadingAllLaps1, selectedTrack);
            fetchAllLaps(selectedDriver2, setAllLapsDriver2, setLoadingAllLaps2, selectedTrack);
        }
    }, [selectedDriver1, selectedDriver2, selectedTrack, analysisMode]);

    // Filtered laps based on range
    const filteredLaps1 = useMemo(() => {
        if (analysisMode !== 'multi' && analysisMode !== 'trends') return [];
        return allLapsDriver1.filter(
            lap => lap.lap_number >= lapRange.start && lap.lap_number <= lapRange.end
        );
    }, [allLapsDriver1, lapRange, analysisMode]);

    const filteredLaps2 = useMemo(() => {
        if (analysisMode !== 'multi' && analysisMode !== 'trends') return [];
        return allLapsDriver2.filter(
            lap => lap.lap_number >= lapRange.start && lap.lap_number <= lapRange.end
        );
    }, [allLapsDriver2, lapRange, analysisMode]);

    // Chart data for sector comparison (Bar chart)
    const sectorChartData = useMemo(() => {
        if (!lapDataDriver1 || !lapDataDriver2 || !selectedDriver1 || !selectedDriver2) return null;
        if (!drivers || drivers.length === 0) return null;

        const driver1 = drivers.find(d => d && d.id === selectedDriver1);
        const driver2 = drivers.find(d => d && d.id === selectedDriver2);

        return {
            labels: ['Sector 1', 'Sector 2', 'Sector 3'],
            datasets: [
                {
                    label: driver1?.name || 'Driver 1',
                    data: [
                        lapDataDriver1.duration_sector_1 || 0,
                        lapDataDriver1.duration_sector_2 || 0,
                        lapDataDriver1.duration_sector_3 || 0,
                    ],
                    backgroundColor: driver1?.color || '#36a2eb',
                },
                {
                    label: driver2?.name || 'Driver 2',
                    data: [
                        lapDataDriver2.duration_sector_1 || 0,
                        lapDataDriver2.duration_sector_2 || 0,
                        lapDataDriver2.duration_sector_3 || 0,
                    ],
                    backgroundColor: driver2?.color || '#ff6384',
                },
            ],
        };
    }, [lapDataDriver1, lapDataDriver2, selectedDriver1, selectedDriver2]);

    // Radar chart data for sector comparison
    const radarChartData = useMemo(() => {
        if (!lapDataDriver1 || !lapDataDriver2 || !selectedDriver1 || !selectedDriver2) return null;
        if (!drivers || drivers.length === 0) return null;

        const driver1 = drivers.find(d => d && d.id === selectedDriver1);
        const driver2 = drivers.find(d => d && d.id === selectedDriver2);

        // Invert times for radar (lower is better, so we show inverse)
        const maxTime = Math.max(
            lapDataDriver1.duration_sector_1 || 0,
            lapDataDriver1.duration_sector_2 || 0,
            lapDataDriver1.duration_sector_3 || 0,
            lapDataDriver2.duration_sector_1 || 0,
            lapDataDriver2.duration_sector_2 || 0,
            lapDataDriver2.duration_sector_3 || 0,
        ) * 1.1;

        return {
            labels: ['Sector 1', 'Sector 2', 'Sector 3'],
            datasets: [
                {
                    label: driver1?.name || 'Driver 1',
                    data: [
                        maxTime - (lapDataDriver1.duration_sector_1 || 0),
                        maxTime - (lapDataDriver1.duration_sector_2 || 0),
                        maxTime - (lapDataDriver1.duration_sector_3 || 0),
                    ],
                    borderColor: driver1?.color || '#36a2eb',
                    backgroundColor: addAlpha(driver1?.color || '#36a2eb', 0.25),
                    borderWidth: 2,
                },
                {
                    label: driver2?.name || 'Driver 2',
                    data: [
                        maxTime - (lapDataDriver2.duration_sector_1 || 0),
                        maxTime - (lapDataDriver2.duration_sector_2 || 0),
                        maxTime - (lapDataDriver2.duration_sector_3 || 0),
                    ],
                    borderColor: driver2?.color || '#ff6384',
                    backgroundColor: addAlpha(driver2?.color || '#ff6384', 0.25),
                    borderWidth: 2,
                },
            ],
        };
    }, [lapDataDriver1, lapDataDriver2, selectedDriver1, selectedDriver2]);

    // Trends chart data
    const trendsChartData = useMemo(() => {
        if (filteredLaps1.length === 0 || filteredLaps2.length === 0) return null;
        if (!drivers || drivers.length === 0 || !selectedDriver1 || !selectedDriver2) return null;

        const driver1 = drivers.find(d => d && d.id === selectedDriver1);
        const driver2 = drivers.find(d => d && d.id === selectedDriver2);

        const lapNumbers1 = filteredLaps1.map(lap => lap.lap_number);
        const lapTimes1 = filteredLaps1.map(lap => lap.lap_duration);
        const lapNumbers2 = filteredLaps2.map(lap => lap.lap_number);
        const lapTimes2 = filteredLaps2.map(lap => lap.lap_duration);

        // Combine lap numbers for x-axis
        const allLapNumbers = [...new Set([...lapNumbers1, ...lapNumbers2])].sort((a, b) => a - b);

        return {
            labels: allLapNumbers,
            datasets: [
                {
                    label: driver1?.name || 'Driver 1',
                    data: allLapNumbers.map(lapNum => {
                        const index = lapNumbers1.indexOf(lapNum);
                        return index !== -1 ? lapTimes1[index] : null;
                    }),
                    borderColor: driver1?.color || '#36a2eb',
                    backgroundColor: addAlpha(driver1?.color || '#36a2eb', 0.25),
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4,
                },
                {
                    label: driver2?.name || 'Driver 2',
                    data: allLapNumbers.map(lapNum => {
                        const index = lapNumbers2.indexOf(lapNum);
                        return index !== -1 ? lapTimes2[index] : null;
                    }),
                    borderColor: driver2?.color || '#ff6384',
                    backgroundColor: addAlpha(driver2?.color || '#ff6384', 0.25),
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4,
                },
            ],
        };
    }, [filteredLaps1, filteredLaps2, selectedDriver1, selectedDriver2]);

    const handleLapChange = (increment) => {
        setCurrentLap((prevLap) => Math.max(prevLap + increment, 1));
    };

    const handleDriver1Change = (event) => {
        setSelectedDriver1(Number(event.target.value));
    };

    const handleDriver2Change = (event) => {
        setSelectedDriver2(Number(event.target.value));
    };

    const handleTrackChange = (event) => {
        setSelectedTrack(event.target.value);
    };

    const handleModeChange = (event) => {
        setAnalysisMode(event.target.value);
    };

    const calculateComparison = () => {
        if (!lapDataDriver1 || !lapDataDriver2) return null;

        return {
            sector1: (lapDataDriver1.duration_sector_1 - lapDataDriver2.duration_sector_1).toFixed(3),
            sector2: (lapDataDriver1.duration_sector_2 - lapDataDriver2.duration_sector_2).toFixed(3),
            sector3: (lapDataDriver1.duration_sector_3 - lapDataDriver2.duration_sector_3).toFixed(3),
            total: (lapDataDriver1.lap_duration - lapDataDriver2.lap_duration).toFixed(3),
        };
    };

    const comparison = calculateComparison();
    const driver1 = drivers && drivers.length > 0 && selectedDriver1 ? drivers.find(d => d && d.id === selectedDriver1) : null;
    const driver2 = drivers && drivers.length > 0 && selectedDriver2 ? drivers.find(d => d && d.id === selectedDriver2) : null;

    // Update lap range when all laps are loaded
    useEffect(() => {
        if (allLapsDriver1.length > 0 || allLapsDriver2.length > 0) {
            const maxLap = Math.max(
                ...allLapsDriver1.map(l => l.lap_number),
                ...allLapsDriver2.map(l => l.lap_number),
                1
            );
            setLapRange(prev => ({ ...prev, end: Math.min(prev.end, maxLap) }));
        }
    }, [allLapsDriver1, allLapsDriver2]);

    return (
        <div className="driver-lap-analysis">
            {loadingTracks ? (
                <Loader />
            ) : (
                <div className="controls-panel">
                    <ModernSelect
                        label="Grand Prix"
                        value={selectedTrack || ''}
                        onChange={handleTrackChange}
                        options={tracks.map(track => ({ value: track.country_name, label: track.track_name }))}
                    />
                    <ModernSelect
                        label="Analysis Mode"
                        value={analysisMode}
                        onChange={handleModeChange}
                        options={analysisModes}
                    />
                    {loadingDrivers ? (
                        <div className="loading-drivers">Loading drivers...</div>
                    ) : (
                        <>
                            <ModernSelect
                                label="Driver 1"
                                value={selectedDriver1 || ''}
                                onChange={handleDriver1Change}
                                options={drivers.map(driver => ({ value: driver.id, label: driver.name }))}
                            />
                            <ModernSelect
                                label="Driver 2"
                                value={selectedDriver2 || ''}
                                onChange={handleDriver2Change}
                                options={drivers.map(driver => ({ value: driver.id, label: driver.name }))}
                            />
                        </>
                    )}
                </div>
            )}

            {analysisMode === 'single' && (
                <>
                    <div className="lap-navigation">
                        <button onClick={() => handleLapChange(-1)} disabled={currentLap - 1 <= 1}>
                            Previous Lap
                        </button>
                        <span className="lap-indicator">Lap {currentLap - 1}</span>
                        <button onClick={() => handleLapChange(1)}>
                            Next Lap
                        </button>
                    </div>

                    <div className="lap-comparison">
                        <div className="driver-section">
                            {loadingDriver1 ? (
                                <Loader />
                            ) : errorDriver1 ? (
                                <p className="error-message">{errorDriver1}</p>
                            ) : lapDataDriver1 ? (
                                <LapDataDisplay
                                    label={driver1?.name}
                                    lapData={lapDataDriver1}
                                    imgSrc={driver1?.img}
                                    driverColor={driver1?.color}
                                />
                            ) : (
                                <p>No lap data available.</p>
                            )}
                        </div>
                        <div className="driver-section">
                            {loadingDriver2 ? (
                                <Loader />
                            ) : errorDriver2 ? (
                                <p className="error-message">{errorDriver2}</p>
                            ) : lapDataDriver2 ? (
                                <LapDataDisplay
                                    label={driver2?.name}
                                    lapData={lapDataDriver2}
                                    imgSrc={driver2?.img}
                                    driverColor={driver2?.color}
                                />
                            ) : (
                                <p>No lap data available.</p>
                            )}
                        </div>
                    </div>

                    {sectorChartData && (
                        <div className="chart-section">
                            <h3 className="section-title">
                                <FaFlagCheckered /> Sector Comparison
                            </h3>
                            <div className="chart-container">
                                <Bar
                                    data={sectorChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: {
                                                display: true,
                                                position: 'top',
                                                labels: { color: '#ffffff' },
                                            },
                                            tooltip: {
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                titleColor: '#ffffff',
                                                bodyColor: '#ffffff',
                                            },
                                            datalabels: {
                                                color: 'white',
                                                anchor: 'end',
                                                align: 'end',
                                                formatter: (value) => `${value.toFixed(3)}s`,
                                            },
                                        },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                title: { display: true, text: 'Duration (s)', color: '#ffffff' },
                                                ticks: { color: '#ffffff' },
                                                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                                            },
                                            x: {
                                                ticks: { color: '#ffffff' },
                                                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                                            },
                                        },
                                    }}
                                    plugins={[ChartDataLabels]}
                                />
                            </div>
                        </div>
                    )}

                    {radarChartData && (
                        <div className="chart-section">
                            <h3 className="section-title">
                                <FaChartLine /> Sector Performance Radar
                            </h3>
                            <div className="chart-container">
                                <Radar
                                    data={radarChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: {
                                                display: true,
                                                position: 'top',
                                                labels: { color: '#ffffff' },
                                            },
                                            tooltip: {
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                titleColor: '#ffffff',
                                                bodyColor: '#ffffff',
                                            },
                                        },
                                        scales: {
                                            r: {
                                                ticks: { color: '#ffffff', backdropColor: 'transparent' },
                                                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                                                pointLabels: { color: '#ffffff' },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {comparison && (
                        <div className="comparison-summary">
                            <h3>Comparison Summary</h3>
                            <div className="comparison-grid">
                                <div className={`comparison-item ${parseFloat(comparison.sector1) < 0 ? 'faster' : ''}`}>
                                    <span className="comparison-label">Sector 1</span>
                                    <span className="comparison-value">
                                        {parseFloat(comparison.sector1) < 0 
                                            ? `${driver1?.name} faster by ${Math.abs(comparison.sector1)}s`
                                            : `${driver2?.name} faster by ${Math.abs(comparison.sector1)}s`}
                                    </span>
                                </div>
                                <div className={`comparison-item ${parseFloat(comparison.sector2) < 0 ? 'faster' : ''}`}>
                                    <span className="comparison-label">Sector 2</span>
                                    <span className="comparison-value">
                                        {parseFloat(comparison.sector2) < 0 
                                            ? `${driver1?.name} faster by ${Math.abs(comparison.sector2)}s`
                                            : `${driver2?.name} faster by ${Math.abs(comparison.sector2)}s`}
                                    </span>
                                </div>
                                <div className={`comparison-item ${parseFloat(comparison.sector3) < 0 ? 'faster' : ''}`}>
                                    <span className="comparison-label">Sector 3</span>
                                    <span className="comparison-value">
                                        {parseFloat(comparison.sector3) < 0 
                                            ? `${driver1?.name} faster by ${Math.abs(comparison.sector3)}s`
                                            : `${driver2?.name} faster by ${Math.abs(comparison.sector3)}s`}
                                    </span>
                                </div>
                                <div className={`comparison-item ${parseFloat(comparison.total) < 0 ? 'faster' : ''}`}>
                                    <span className="comparison-label">Total Lap</span>
                                    <span className="comparison-value">
                                        {parseFloat(comparison.total) < 0 
                                            ? `${driver1?.name} faster by ${Math.abs(comparison.total)}s overall`
                                            : `${driver2?.name} faster by ${Math.abs(comparison.total)}s overall`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {analysisMode === 'multi' && (
                <>
                    <div className="range-selector">
                        <label>Lap Range: </label>
                        <input
                            type="number"
                            min="1"
                            value={lapRange.start}
                            onChange={(e) => setLapRange(prev => ({ ...prev, start: parseInt(e.target.value) || 1 }))}
                            className="range-input"
                        />
                        <span> to </span>
                        <input
                            type="number"
                            min="1"
                            value={lapRange.end}
                            onChange={(e) => setLapRange(prev => ({ ...prev, end: parseInt(e.target.value) || 20 }))}
                            className="range-input"
                        />
                    </div>

                    {loadingAllLaps1 || loadingAllLaps2 ? (
                        <Loader />
                    ) : (
                        <>
                            <StatisticsPanel lapData={filteredLaps1} driverName={driver1?.name} />
                            <StatisticsPanel lapData={filteredLaps2} driverName={driver2?.name} />
                            <PaceAnalysis lapData={filteredLaps1} driverName={driver1?.name} driverColor={driver1?.color} />
                            <PaceAnalysis lapData={filteredLaps2} driverName={driver2?.name} driverColor={driver2?.color} />
                        </>
                    )}
                </>
            )}

            {analysisMode === 'trends' && (
                <>
                    <div className="range-selector">
                        <label>Lap Range: </label>
                        <input
                            type="number"
                            min="1"
                            value={lapRange.start}
                            onChange={(e) => setLapRange(prev => ({ ...prev, start: parseInt(e.target.value) || 1 }))}
                            className="range-input"
                        />
                        <span> to </span>
                        <input
                            type="number"
                            min="1"
                            value={lapRange.end}
                            onChange={(e) => setLapRange(prev => ({ ...prev, end: parseInt(e.target.value) || 20 }))}
                            className="range-input"
                        />
                    </div>

                    {loadingAllLaps1 || loadingAllLaps2 ? (
                        <Loader />
                    ) : trendsChartData ? (
                        <div className="chart-section">
                            <h3 className="section-title">
                                <FaChartLine /> Lap Time Trends
                            </h3>
                            <div className="chart-container trends-chart">
                                <Line
                                    data={trendsChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: {
                                                display: true,
                                                position: 'top',
                                                labels: { color: '#ffffff' },
                                            },
                                            tooltip: {
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                titleColor: '#ffffff',
                                                bodyColor: '#ffffff',
                                            },
                                        },
                                        scales: {
                                            x: {
                                                title: { display: true, text: 'Lap Number', color: '#ffffff' },
                                                ticks: { color: '#ffffff' },
                                                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                                            },
                                            y: {
                                                title: { display: true, text: 'Lap Time (s)', color: '#ffffff' },
                                                ticks: { color: '#ffffff' },
                                                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <p>No trend data available</p>
                    )}
                </>
            )}
        </div>
    );
};

const LapDataDisplay = ({ label, lapData, imgSrc, driverColor }) => (
    <div className="lap-data-display">
        <div className="driver-header">
            {imgSrc ? (
                <img src={imgSrc} alt={label} className="driver-headshot" />
            ) : (
                <div className="driver-headshot-placeholder" style={{ backgroundColor: driverColor || '#36a2eb' }}>
                    {label ? label.charAt(0).toUpperCase() : '?'}
                </div>
            )}
            <div>
                <h2 className="driver-name">{label}</h2>
                <p className="lap-number">Lap {lapData.lap_number - 1}</p>
            </div>
        </div>
        <div className="lap-stats">
            <div className="lap-stat-item">
                <span className="stat-label">Total Duration</span>
                <span className="stat-value-main">{lapData.lap_duration.toFixed(3)}s</span>
            </div>
            <div className="sector-breakdown">
                <div className="sector-item">
                    <span className="sector-label">S1</span>
                    <span className="sector-value">{lapData.duration_sector_1.toFixed(3)}s</span>
                </div>
                <div className="sector-item">
                    <span className="sector-label">S2</span>
                    <span className="sector-value">{lapData.duration_sector_2.toFixed(3)}s</span>
                </div>
                <div className="sector-item">
                    <span className="sector-label">S3</span>
                    <span className="sector-value">{lapData.duration_sector_3.toFixed(3)}s</span>
                </div>
            </div>
        </div>
    </div>
);

export default DriverLapAnalysis;
